import {
    __,
    set,
    intersectionBy,
    map,
    minBy,
    size,
    reduce,
    each,
    find,
    flatten,
    times,
    get,
    isEmpty,
    filter,
    getOr,
    groupBy,
    entries,
    values,
    over,
    compose,
    some,
    nth,
} from 'lodash/fp';
import { flow, pipe } from 'fp-ts/function';
import { record, string, array } from 'fp-ts';
import { createSelector } from 'reselect';
import {
    composeProps,
    createPropsExtender,
    createScopedSelector,
    multiplyNumbersAsDecimals,
    sumNumbersAsDecimals,
    DateService,
    DateTimeService,
    DateTimeUtcService,
    TimeService,
    TimeUnitUtils,
    Date,
    AppClockService,
    DateTime,
} from '@mews/framework';
import { BusinessSegment, RateType } from '@mews-commander/data-access';
import { TimeUnit } from '@mews/enums';
import { getProp, getStringProp } from '@mews/fp';
import { ApplicationState, Interval, isDefiniteState, WithScope, WithServiceId } from '@mews-commander/types';
import { getContainer } from '@mews-commander/legacy-app-instance';
import {
    mapValuesIndexed,
    dummyAmount,
    multiplyAmount,
    sumAllAmounts,
    getAppDateTimeNow,
    doesUtcIntervalContain,
} from '@mews-commander/utils';
import { getResourceCategoryCapacity } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import { getRates } from '@mews-commander/legacy-entities/src/ducks/rates';
import {
    getBookableServicesByIds,
    isDayUseService,
    getServiceArrivalTime,
    getServiceTimeUnit,
    getTimeUnitByServiceId,
} from '@mews-commander/legacy-entities/src/ducks/services';
import { getVouchers } from '@mews-commander/legacy-entities/src/ducks/vouchers';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getServiceEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/services';
import { BookableService } from '@mews-commander/legacy-entities/src/ducks/servicesNew';
import { moduleSelector } from '../module';
import { existsDateRangeCollision, getDateTime } from '../utils/dates';
import { mapProductCountsToArray } from '../utils/products';
import { INITIAL_STATE } from '../reducers/reservations';
import { ALLOWED_OVERBOOK_COUNT } from '../constants';
import { createDateRangeKey } from '../utils/keys';
import { ReservationOrder } from '../types';
import { getOrder } from './order';
import { getProducts, getTotalPrices } from './basket';
import { getOptionValues, getState, shouldSendConfirmation } from './options';
import { getSelectedService, canOverbookSelectedService } from './entities';
import {
    getSelectedServiceReleasedUtc,
    getDateTimeUtcString,
    getEndDateTime,
    getStartDateTime,
} from './dateConversions';
import { getSelectedAvailabilityBlockId } from './screens';

const getReservationScopes = createSelector(moduleSelector, get('reservations'));
export const getReservations = createScopedSelector({
    parentSelector: getReservationScopes,
    initialState: INITIAL_STATE,
});

interface ReservationValue {
    count: number;
    categoryId: string;
}

export const getReservationKeys = flow(getReservations, (reservations) => reservations.reservationKeys);
// todo separate keys and values
export const getReservationValues = flow(getReservations, ({ reservationKeys, ...rest }) => rest);
export const hasAnyReservation = flow(getReservationValues, (reservations) => size(reservations) > 0);

export const getReservationsTotalPrice = createSelector(
    getReservationValues,
    getTotalPrices,
    (reservationValues, prices) =>
        sumAllAmounts(
            map(({ count, identifier }) => {
                const price = prices[identifier] || dummyAmount;
                // @ts-expect-error
                return multiplyAmount(price, count);
            }, reservationValues)
        )
);

export const getReservationsTaxes = compose(
    ([reservationValues, prices]) =>
        reduce(
            (acc, { count, identifier }) => {
                const price = prices[identifier] || dummyAmount;
                const { taxValues } = price;

                each(({ taxRateCode, value }) => {
                    // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
                    acc[taxRateCode] = sumNumbersAsDecimals(
                        // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
                        acc[taxRateCode] || 0,
                        multiplyNumbersAsDecimals(value, count)
                    );
                }, taxValues);

                return acc;
            },
            {},
            reservationValues
        ),
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    over([getReservationValues, getTotalPrices])
);

export const getCategoryReservationsTotalCount = createSelector(
    getReservationValues,
    getStringProp('categoryId'),
    (reservationValues: Array<ReservationValue>, categoryId) =>
        compose(
            reduce((acc, r: ReservationValue) => acc + r.count, 0),
            filter<ReservationValue>({ categoryId })
        )(reservationValues)
);

export const getOverbookableCount: (
    state: ApplicationState,
    params: WithServiceId & WithScope & { categoryId?: string; reservationKey?: string }
) => number = createSelector(canOverbookSelectedService, (canOverbook) => (canOverbook ? ALLOWED_OVERBOOK_COUNT : 0));

export const getReservationTotals = createSelector(getReservationValues, (reservationValues) => {
    const countTotal = reduce((acc, r: ReservationValue) => acc + r.count, 0)(reservationValues);
    return {
        countTotal,
    };
});

export const getReservationCumulatedOccupancyData = compose(
    mapValuesIndexed((sameAgeCategoryOccupancyData: $TSFixMe, ageCategoryId: $TSFixMe) =>
        reduce(
            (acc, { personCount }) => {
                acc.personCount += personCount;
                return acc;
            },
            { ageCategoryId, personCount: 0 },
            sameAgeCategoryOccupancyData
        )
    ),
    groupBy('ageCategoryId'),
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    flatten,
    map(({ occupancyData, count }) =>
        compose(
            map(({ ageCategoryId, personCount }) => ({ ageCategoryId, personCount: personCount * count })),
            values
        )(occupancyData)
    ),
    getReservationValues
);

export const getReservationsMinimalStartDateTime = createSelector(
    getReservationValues,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getProp<AppClockService>()('appClockService'),
    (reservations, timezone: string, appClockService) => {
        if (isEmpty(reservations)) {
            return getAppDateTimeNow({ appClockService, timezone });
        }
        return pipe(
            reservations,
            map(({ startDate, startTime }: { startDate: string; startTime?: string }) => {
                const parsedStartDate = DateService.parse(startDate, timezone);
                const parsedStartTime = startTime ? TimeService.parse(startTime) : TimeService.parse('00:00:00.000');
                return DateTimeService.fromDateAndTime(parsedStartDate, parsedStartTime);
            }),
            minBy((dateTime: DateTime) => DateTimeService.valueOf(dateTime))
        );
    }
);

export const getReservationsMinimalStartDate = createSelector(
    getReservationValues,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getProp<AppClockService>()('appClockService'),
    (reservations, timezone: string, appClockService) => {
        if (isEmpty(reservations)) {
            const now = getAppDateTimeNow({ appClockService, timezone });
            return DateTimeService.toDate(now);
        }

        return compose(
            minBy((date: Date) => DateService.valueOf(date)),
            map(({ startDate }) => DateService.parse(startDate, timezone))
        )(reservations);
    }
);

export const getReservationsMinimalArrivalUtc = createSelector(
    getReservationsMinimalStartDate,
    getServiceArrivalTime,
    (state, { id }) => getTimeUnitByServiceId(state, { serviceId: id }),
    getReservationsMinimalStartDateTime,
    getProp<AppClockService>()('appClockService'),
    (date, timeString, timeUnit, hourlyDateTime, appClockService) => {
        if (timeUnit === TimeUnit.Hour) {
            if (!hourlyDateTime) {
                return appClockService.getAppNowUtc();
            }
            return DateTimeUtcService.fromEpoch(hourlyDateTime.millis);
        }
        if (!date || !timeString) {
            return appClockService.getAppNowUtc();
        }
        const datetime = DateTimeService.fromDateAndTime(date, TimeService.parse(timeString));
        return DateTimeUtcService.fromEpoch(datetime.millis);
    }
);

export const getDefaultReleaseDateString = createSelector(
    getReservationsMinimalStartDate,
    getContainer,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getProp<AppClockService>()('appClockService'),
    (minimalStartDate, container, timezone: string, appClockService) => {
        const { appLocalizationComponent } = container();

        const now = getAppDateTimeNow({ appClockService, timezone });

        return (
            minimalStartDate &&
            compose(
                appLocalizationComponent.localizeDate,
                (date: Date) => DateTimeService.fromDate(date),
                (days) => DateService.addDays(minimalStartDate, days),
                (diff: number) => Math.floor(diff / 2),
                (today) => DateService.diffDays(today, minimalStartDate),
                DateTimeService.toDate
            )(now)
        );
    }
);

export const getReservation = createSelector(
    getReservationValues,
    getStringProp('reservationKey'),
    (reservations, key) => reservations[key] || {}
);

export const getReservationInfoForBox = createSelector(
    getReservation,
    getResourceCategoryCapacity,
    getRates,
    getVouchers,
    getProp<Record<string, BusinessSegment>>()('businessSegments'),
    (
        {
            productIds,
            rateId,
            adultCount,
            childCount,
            voucherId,
            purpose,
            businessSegmentId,
            channelNumber,
            customNightCostToShow,
            occupancyData,
        },
        capacity,
        rates,
        vouchers,
        businessSegments
    ) => {
        const { name: rateName, shortName: rateShortName } = rates[rateId];
        // @ts-expect-error $TSFixMeError: Property 'name' does not exist on type '{}'.
        const { name: voucherName } = getOr({}, voucherId)(vouchers);
        // @ts-expect-error $TSFixMeError: Property 'name' does not exist on type '{}'.
        const { name: segmentName } = getOr({}, businessSegmentId)(businessSegments);
        // todo company & agency - requires search to go through entities

        const personCount = reduce((acc, c: { personCount: number }) => acc + c.personCount, 0, occupancyData);

        return {
            channelNumber,
            rateName,
            rateShortName,
            voucherName,
            purpose,
            segmentName,
            productIds,
            adultCount,
            childCount,
            customNightCostToShow,
            extraBeds: capacity ? Math.max(personCount - capacity, 0) : 0,
            occupancyData,
        };
    }
);

export const getReservationCategoryCount = createSelector(getReservation, (r) => r.count);

const mapProductCounts = compose(mapProductCountsToArray, get);

const getReservationServiceIds = compose(map('serviceId'), getReservationValues);

const getBookedServices = composeProps(getBookableServicesByIds, createPropsExtender('ids', getReservationServiceIds));

export const getReservationGroup = createSelector(
    getSelectedService,
    getBookedServices,
    getServiceEnterpriseIanaTimeZoneIdentifier,
    getOptionValues,
    getState,
    getSelectedServiceReleasedUtc,
    getReservationValues,
    shouldSendConfirmation,
    getProducts,
    getSelectedAvailabilityBlockId,
    (
        service,
        reservationsServices,
        ianaTimeZoneIdentifier,
        {
            reservationGroupId,
            customerId,
            bookerId,
            partnerCompanyId,
            travelAgencyId,
            origin,
            notes,
            confirmationEmailReceivers,
            confirmationLanguageCode,
        },
        state,
        releasedUtc,
        reservationValues,
        sendConfirmation,
        productCounts,
        availabilityBlockId
    ) => {
        const { enterpriseId } = service;
        const isDefinite = isDefiniteState(state);

        const reservations = compose(
            flatten,
            map(
                ({
                    identifier,
                    count,
                    total,
                    categoryId,
                    startDate,
                    endDate,
                    startTime,
                    endTime,
                    channelNumber,
                    productIds,
                    serviceId,
                    requestedResourceIds,
                    requestedFloorNumbers,
                    ...other
                }) => {
                    // @ts-expect-error $TSFixMeError: Property 'arrivalTime' does not exist on type 'number | { data: any; } | (() => IterableIterator<{ data: any; }>) | (() ...
                    const { arrivalTime, departureTime } = find({ id: serviceId }, reservationsServices);

                    const r = {
                        ...other,
                        identifier,
                        serviceId,
                        startUtc: getDateTimeUtcString(startDate, startTime || arrivalTime, ianaTimeZoneIdentifier),
                        endUtc: getDateTimeUtcString(endDate, endTime || departureTime, ianaTimeZoneIdentifier),
                        partnerCompanyId,
                        travelAgencyId,
                        requestedCategoryId: categoryId,
                        state,
                        origin,
                        notes: isEmpty(notes) ? null : notes,
                        channelNumber: isEmpty(channelNumber) ? null : channelNumber,
                        // @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 2.
                        productOrders: mapProductCounts(identifier, productCounts),
                        availabilityBlockId,
                    };
                    if (!isDefinite) {
                        (r as $TSFixMe).releasedUtc = releasedUtc;
                    }

                    return times((index) => {
                        const requestedResourceId = nth(index, requestedResourceIds);
                        // Just a temporarily commented out so the backend can adapt the resource assignment algorithm
                        // const floorNumbers = nth(index, requestedFloorNumbers);
                        return {
                            ...r,
                            // @ts-expect-error $TSFixMeError: Spread types may only be created from object types.
                            ...(requestedResourceId && { requestedResourceId }),
                            // ...(!isEmpty(floorNumbers) && { requestedFloorNumbers: floorNumbers }),
                        };
                    })(count);
                }
            )
        )(reservationValues);

        return {
            groupId: reservationGroupId,
            enterpriseId,
            customerId,
            bookerId,
            sendEmail: sendConfirmation,
            mailReceivers: confirmationEmailReceivers,
            customLanguageCode: sendConfirmation && confirmationLanguageCode ? confirmationLanguageCode : null,
            reservations,
        };
    }
);

export const getReservationValuesGroupedByDateRange = createSelector(
    getReservationValues,
    getReservationKeys,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    (reservationValues, reservationKeys, timezone: string) =>
        compose(
            map(([dateRange, reservations]) => ({
                dateRange,
                reservations,
                // TODO this is potentially wrong - it will be shown as date in the CURRENT hotel timezone, but applied as date in TARGET hotel timezone
                // This can be confusing on the edge cases?
                startDate: getDateTime(reservations[0].startDate, reservations[0].startTime, timezone),
                endDate: getDateTime(reservations[0].endDate, reservations[0].endTime, timezone),
            })),
            entries,
            groupBy((item) => createDateRangeKey(item)),
            map((key) => ({
                // @ts-expect-error $TSFixMeError: Type 'unknown' cannot be used as an index type.
                ...reservationValues[key],
                reservationKey: key,
            }))
        )(reservationKeys)
);

export const getReservationRateIds = createSelector(getReservations, compose(map(get('rateId')), values));

export const getReservationPrivateRates = createSelector(getReservationRateIds, getRates, (reservationRateIds, rates) =>
    compose(
        intersectionBy('id', __, map(set('id', __, {}), reservationRateIds)),
        // @ts-expect-error $TSFixMeError: No overload matches this call.
        filter(['type', RateType.Private]),
        values
        // @ts-expect-error $TSFixMeError: Expected 0 arguments, but got 1.
    )(rates)
);

export const getCollidingReservationsByCategory = createSelector(
    getOrder,
    getReservationValues,
    (order, reservations) => compose(groupBy('categoryId'), filter(existsDateRangeCollision(order)))(reservations)
);

export const getReservationTimeUnitCount = createSelector(
    getStartDateTime,
    getEndDateTime,
    isDayUseService,
    getServiceTimeUnit,
    (startDate, endDate, isDayService, timeUnit) => {
        const greedy = isDayService || timeUnit === TimeUnit.Month;
        if (!timeUnit || !startDate || !endDate) {
            return 0;
        }
        const timePart = TimeUnitUtils.timeUnitToDateTimePart(timeUnit);
        return TimeUnitUtils.dateTimeToTimeUnitCount({ timePart, greedy })(startDate, endDate);
    }
);

export const hasAnyBookedServiceSendConfirmationEmailByDefault = compose(
    some(({ data }: BookableService) => !!data.value?.visitOptions.sendConfirmationEmailByDefault),
    getBookedServices
);

export const getReservationRequestedResourceIds = (
    state: ApplicationState,
    props: WithScope & WithServiceId & Interval
): string[] => {
    const reservationOrders: Record<string, ReservationOrder> = getReservationValues(state, props);
    const { arrivalTime, departureTime, id } = getSelectedService(state, { scope: props.scope });
    const ianaTimeZoneIdentifier = getServiceEnterpriseIanaTimeZoneIdentifier(state, { serviceId: id });

    const filterOrdersInSelectedIntervals = (order: ReservationOrder) => {
        const startUtc = getDateTimeUtcString(order.startDate, order.startTime || arrivalTime, ianaTimeZoneIdentifier);
        const endUtc = getDateTimeUtcString(order.endDate, order.endTime || departureTime, ianaTimeZoneIdentifier);
        return doesUtcIntervalContain(props, { startUtc, endUtc });
    };

    return pipe(
        reservationOrders,
        record.filter(filterOrdersInSelectedIntervals),
        record.collect(string.Ord)((_, { requestedResourceIds }) => requestedResourceIds),
        array.flatten,
        array.filter((item) => item != null)
    );
};
